import { apiClient } from './api-client';
import type { AddressSelection, AddressSource } from './equipment-intake.service';

// ── Phase 2a-2e: Bulk Excel Equipment Intake / Import ──────────────────────
// Mirrors the exact backend contract (docs/reports/architecture/
// BULK_EXCEL_EQUIPMENT_INTAKE_IMPORT_ARCHITECTURE_AND_PHASE2_READINESS_20260711.md
// §40) implemented through Phase 2e. Commit/finalize/cancel/report endpoints
// do not exist on the backend yet (Phase 2f+) — no client method is added
// for them.

export type BulkImportTargetMode = 'NEW_LOT' | 'EXISTING_LOT';

export type BulkImportSessionStatus =
  | 'UPLOADED'
  | 'VALIDATING'
  | 'REVIEW_REQUIRED'
  | 'COMMITTING'
  | 'PARTIALLY_COMPLETED'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'
  | 'EXPIRED';

export type BulkImportRowClassification =
  'PASS_EXISTING' | 'PASS_NEW' | 'WARNING' | 'FAIL' | 'CONFLICT';

/**
 * The intake header, stated ONCE for the whole delivery ("one truck = one
 * header"). Addresses travel as SELECTIONS, never as address text — the server
 * resolves and freezes the snapshot. Reuses AddressSelection from the intake
 * service rather than declaring a parallel shape.
 */
export interface BulkImportSession {
  id: string;
  customerId: string;
  customerLocationId: string | null;
  complaintDescription: string;
  customerContactId: string | null;
  billToSource: AddressSource | null;
  billToLocationId: string | null;
  shipToSource: AddressSource | null;
  shipToLocationId: string | null;
  priorityId: string | null;
  notes: string | null;
  targetMode: BulkImportTargetMode;
  targetReceivingLotId: string | null;
  status: BulkImportSessionStatus;
  totalRows: number;
  passExistingCount: number;
  passNewCount: number;
  warningCount: number;
  failCount: number;
  conflictCount: number;
  sourceFileName: string | null;
  sourceFileHash: string | null;
  resultingReceivingLotId: string | null;
  resultingArrivalId: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBulkImportSessionPayload {
  customerId: string;
  customerLocationId?: string;
  /** Reason for Receipt — mandatory. Never send `notes` in its place. */
  complaintDescription: string;
  customerContactId?: string;
  billTo?: AddressSelection;
  shipTo?: AddressSelection;
  priorityId?: string;
  notes?: string;
  targetMode: BulkImportTargetMode;
  targetReceivingLotId?: string;
  createIdempotencyKey: string;
}

export interface BulkImportRowListItem {
  id: string;
  rowNumber: number;
  classification: BulkImportRowClassification;
  matchedEquipmentId: string | null;
  revalidationVersion: number;
  issueCount: number;
  correctionCount: number;
  committed: boolean;
  abandoned: boolean;
  serialNumber: string | null;
  assetNumber: string | null;
  equipmentName: string | null;
}

export interface BulkImportRowListResponse {
  data: BulkImportRowListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface BulkImportRowListParams {
  page?: number;
  pageSize?: number;
  classification?: BulkImportRowClassification;
  errorCode?: string;
  resolvedEquipmentModelId?: string;
  search?: string;
}

export interface BulkImportRowIssue {
  id: string;
  severity: 'ERROR' | 'WARNING';
  code: string;
  field: string | null;
  message: string;
  revalidationVersion: number;
  createdAt: string;
}

export interface BulkImportCorrection {
  id: string;
  field: string;
  previousValue: string | null;
  newValue: string | null;
  correctedByUserId: string;
  createdAt: string;
}

export interface BulkImportRowDetail {
  id: string;
  rowNumber: number;
  classification: BulkImportRowClassification;
  rawValuesJson: Record<string, unknown>;
  effectiveValuesJson: Record<string, unknown>;
  resolvedManufacturerId: string | null;
  resolvedEquipmentTypeId: string | null;
  resolvedEquipmentModelId: string | null;
  matchedEquipmentId: string | null;
  proposedEquipmentJson: Record<string, unknown> | null;
  revalidationVersion: number;
  committedEquipmentId: string | null;
  abandonedAt: string | null;
  issues: BulkImportRowIssue[];
  corrections: BulkImportCorrection[];
  createdAt: string;
  updatedAt: string;
}

/** Exactly the backend-approved 9 editable fields (§27) — never any other
 * key, matching the server's own allowlist verbatim. */
export const EDITABLE_ROW_FIELDS = [
  'serialNumber',
  'assetNumber',
  'equipmentName',
  'manufacturer',
  'modelNumber',
  'equipmentType',
  'accessories',
  'purchaseDate',
  'notes',
] as const;
export type EditableRowField = (typeof EDITABLE_ROW_FIELDS)[number];

export interface CorrectRowPayload {
  field: EditableRowField;
  value: string | null;
}

export interface RevalidateSessionPayload {
  rowIds?: string[];
}

export interface RevalidateSessionResult {
  totalRows: number;
  passExistingCount: number;
  passNewCount: number;
  warningCount: number;
  failCount: number;
  conflictCount: number;
  revalidatedRowCount: number;
}

/** Mirrors CommitBulkImportSessionDto exactly (backend §40, D-066/D-068). */
export interface CommitSessionPayload {
  commitIdempotencyKey: string;
  acknowledgeWarnings?: string[];
}

const base = '/bulk-import/sessions';

export const bulkImportService = {
  create: (payload: CreateBulkImportSessionPayload): Promise<BulkImportSession> =>
    apiClient.post<BulkImportSession>(base, payload).then((r) => r.data),

  get: (id: string): Promise<BulkImportSession> =>
    apiClient.get<BulkImportSession>(`${base}/${id}`).then((r) => r.data),

  /** Streams the server-generated, authoritative `.xlsx` template — no
   * client-generated template is ever produced (§27/§28). */
  downloadTemplate: async (params: {
    customerId: string;
    customerLocationId?: string;
    targetMode: BulkImportTargetMode;
    targetReceivingLotId?: string;
  }): Promise<void> => {
    const response = await apiClient.get('/bulk-import/template', {
      params,
      responseType: 'blob',
    });
    const disposition = (response.headers as Record<string, string> | undefined)?.[
      'content-disposition'
    ];
    const match = disposition ? /filename="?([^";]+)"?/i.exec(disposition) : null;
    const filename = match?.[1] ?? 'bulk-import-template.xlsx';
    const url = URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  upload: (id: string, file: File): Promise<BulkImportSession> => {
    const form = new FormData();
    form.append('file', file);
    return apiClient
      .post<BulkImportSession>(`${base}/${id}/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  listRows: (id: string, params: BulkImportRowListParams): Promise<BulkImportRowListResponse> =>
    apiClient.get<BulkImportRowListResponse>(`${base}/${id}/rows`, { params }).then((r) => r.data),

  getRow: (id: string, rowId: string): Promise<BulkImportRowDetail> =>
    apiClient.get<BulkImportRowDetail>(`${base}/${id}/rows/${rowId}`).then((r) => r.data),

  correctRow: (
    id: string,
    rowId: string,
    payload: CorrectRowPayload,
  ): Promise<BulkImportRowDetail> =>
    apiClient
      .patch<BulkImportRowDetail>(`${base}/${id}/rows/${rowId}`, payload)
      .then((r) => r.data),

  revalidate: (id: string, payload: RevalidateSessionPayload): Promise<RevalidateSessionResult> =>
    apiClient
      .post<RevalidateSessionResult>(`${base}/${id}/revalidate`, payload)
      .then((r) => r.data),

  /** Phase 2f (§21/§32-§37/§40, D-066): starts or replays a commit attempt.
   * The response reflects the session's state at request-completion time
   * (often still `COMMITTING` — the chunked write itself runs in a
   * background worker) — callers must keep polling `get()` for the
   * eventual terminal-ish state (`COMPLETED`/`PARTIALLY_COMPLETED`/`FAILED`),
   * exactly like the existing `VALIDATING` poll already does. */
  commit: (id: string, payload: CommitSessionPayload): Promise<BulkImportSession> =>
    apiClient.post<BulkImportSession>(`${base}/${id}/commit`, payload).then((r) => r.data),

  /** Phase 2f (§21/§45, D-066): Admin/Owner-only, `PARTIALLY_COMPLETED`
   * sessions only — synchronous, no body. Permanently abandons every
   * remaining unresolved row and transitions the session to `COMPLETED`. */
  finalize: (id: string): Promise<BulkImportSession> =>
    apiClient.post<BulkImportSession>(`${base}/${id}/finalize`).then((r) => r.data),
};
